/**
 * CommentList — Displays ticket comments with pagination and add-comment form.
 * Internal comments are shown with a distinct style (agents only).
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { supportApi, type Comment } from '../api/support';
import { formatDistanceToNow } from 'date-fns';

interface Props {
  ticketId: string;
  /** When true the user can see and create internal (agent-only) comments. */
  isAgent?: boolean;
}

export function CommentList({ ticketId, isAgent = false }: Props) {
  const queryClient = useQueryClient();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['comments', ticketId, cursor],
    queryFn: () => supportApi.listComments(ticketId, cursor),
    onSuccess: (res) => {
      setAllComments((prev) => {
        const ids = new Set(prev.map((c) => c.id));
        return [...prev, ...res.comments.filter((c) => !ids.has(c.id))];
      });
    },
  });

  const addMutation = useMutation({
    mutationFn: () => supportApi.addComment(ticketId, body, internal),
    onSuccess: (comment) => {
      setAllComments((prev) => [...prev, comment]);
      setBody('');
      setInternal(false);
      setAddError(null);
      void queryClient.invalidateQueries({ queryKey: ['comments', ticketId] });
    },
    onError: (err) => {
      setAddError((err as Error).message ?? 'Failed to add comment');
    },
  });

  function handleLoadMore() {
    if (data?.next_cursor) {
      setCursor(data.next_cursor);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) {
      setAddError('body is required');
      return;
    }
    setAddError(null);
    addMutation.mutate();
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700">Comments</h3>

      {isLoading && allComments.length === 0 && (
        <p className="text-sm text-gray-400 italic">Loading comments…</p>
      )}
      {isError && (
        <p className="text-sm text-red-500">Failed to load comments.</p>
      )}

      {allComments.length === 0 && !isLoading && (
        <p className="text-sm text-gray-400 italic">No comments yet.</p>
      )}

      <ul className="space-y-3">
        {allComments.map((c) => (
          <li
            key={c.id}
            className={`rounded-lg border p-3 text-sm ${
              c.is_internal
                ? 'border-amber-200 bg-amber-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 whitespace-pre-wrap break-words">{c.body}</div>
              {c.is_internal && (
                <span className="flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700 shrink-0">
                  <Lock className="w-3 h-3" /> Internal
                </span>
              )}
            </div>
            <p className="mt-1.5 text-xs text-gray-400">
              {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
            </p>
          </li>
        ))}
      </ul>

      {data?.next_cursor && (
        <button
          type="button"
          onClick={handleLoadMore}
          className="text-xs text-indigo-600 hover:underline"
        >
          Load more comments…
        </button>
      )}

      {/* Add comment form */}
      <form onSubmit={handleSubmit} className="space-y-2 pt-2 border-t border-gray-200">
        {addError && (
          <p className="text-xs text-red-600">{addError}</p>
        )}
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a comment…"
          maxLength={5000}
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
        />
        <div className="flex items-center justify-between">
          {isAgent && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={internal}
                onChange={(e) => setInternal(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Lock className="w-3 h-3" /> Internal note
            </label>
          )}
          <div className="ml-auto">
            <button
              type="submit"
              disabled={addMutation.isPending}
              className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {addMutation.isPending ? 'Posting…' : 'Add Comment'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CommentList;
